import React, { useState, useEffect } from 'react';
import { Itinerary, ItineraryCreate, ItineraryUpdate, AudioStep, LanguageData, SUPPORTED_LANGUAGES } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { GoogleMapsSearch } from './GoogleMapsSearch';
import FileUpload from './FileUpload';
import AudioStepUpload from './AudioStepUpload';
import RichTextEditor from './RichTextEditor';
import { TranslationHelper } from './TranslationHelper';
import { ChevronDown, ChevronRight, Trash2, GripVertical } from 'lucide-react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';

interface ItineraryFormProps {
  itinerary?: Itinerary;
  onSubmit: (data: ItineraryCreate | ItineraryUpdate) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const ItineraryForm: React.FC<ItineraryFormProps> = ({
  itinerary,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    logo: '',
    visibility: 'public' as 'public' | 'restricted',
    visibility_strategy: [] as string[],
  });

  const [audioSteps, setAudioSteps] = useState<AudioStep[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [languageData, setLanguageData] = useState<Record<string, LanguageData>>(() => {
    const initialData: Record<string, LanguageData> = {};
    Object.keys(SUPPORTED_LANGUAGES).forEach(lang => {
      initialData[lang] = {
        welcome: { title: '', subtitle: '' },
      };
    });
    return initialData;
  });

  useEffect(() => {
    if (itinerary) {
      // Parse visibility_strategy from comma-separated string or array
      let strategies: string[] = [];
      
      if (itinerary.visibility_strategy) {
        if (Array.isArray(itinerary.visibility_strategy)) {
          strategies = itinerary.visibility_strategy
            .map(s => String(s).trim())
            .filter(s => s.length > 0);
        } else if (typeof itinerary.visibility_strategy === 'string') {
          strategies = itinerary.visibility_strategy
            .split(',')
            .map(s => String(s).trim())
            .filter(s => s.length > 0);
        }
      }
      
      // Remove duplicates
      strategies = Array.from(new Set(strategies));

      setFormData({
        title: itinerary.title,
        description: itinerary.description || '',
        logo: itinerary.data.logo,
        visibility: itinerary.visibility,
        visibility_strategy: strategies,
      });
      setAudioSteps(itinerary.data.audioSteps);
      setLanguageData(itinerary.data.languageData);
    }
  }, [itinerary]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Deduplicate strategies before saving
    const uniqueStrategies = Array.isArray(formData.visibility_strategy)
      ? Array.from(new Set(formData.visibility_strategy.map(s => String(s).trim()).filter(s => s.length > 0)))
      : [];
    
    const data = {
      title: formData.title,
      description: formData.description || undefined,
      data: {
        audioSteps,
        languageData,
        logo: formData.logo,
      },
      visibility: formData.visibility,
      visibility_strategy: uniqueStrategies.length > 0 ? uniqueStrategies : undefined,
    };

    onSubmit(data);
  };

  const addAudioStep = () => {
    const newStepId = audioSteps.length + 1;
    const newStep: AudioStep = {
      id: newStepId,
      title: Object.keys(SUPPORTED_LANGUAGES).reduce((acc, lang) => {
        acc[lang] = '';
        return acc;
      }, {} as Record<string, string>),
      description: Object.keys(SUPPORTED_LANGUAGES).reduce((acc, lang) => {
        acc[lang] = '';
        return acc;
      }, {} as Record<string, string>),
      audioUrl: Object.keys(SUPPORTED_LANGUAGES).reduce((acc, lang) => {
        acc[lang] = '';
        return acc;
      }, {} as Record<string, string>),
      imageUrl: '',
      location: { latitude: 0, longitude: 0 },
    };
    setAudioSteps([...audioSteps, newStep]);
    // Auto-expand the newly added step
    setExpandedSteps(prev => new Set([...Array.from(prev), newStepId]));
  };

  const updateAudioStep = (index: number, field: keyof AudioStep, value: any) => {
    const updatedSteps = [...audioSteps];
    if (field === 'title' || field === 'description' || field === 'audioUrl') {
      updatedSteps[index] = {
        ...updatedSteps[index],
        [field]: {
          ...updatedSteps[index][field],
          ...value,
        },
      };
    } else {
      updatedSteps[index] = {
        ...updatedSteps[index],
        [field]: value,
      };
    }
    setAudioSteps(updatedSteps);
  };

  const removeAudioStep = (index: number) => {
    const stepToRemove = audioSteps[index];
    setAudioSteps(audioSteps.filter((_, i) => i !== index));
    // Remove from expanded steps if it was expanded
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(stepToRemove.id);
      return newSet;
    });
  };

  const toggleStepExpansion = (stepId: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = Array.from(audioSteps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setAudioSteps(items);
  };

  const updateLanguageData = (lang: string, section: string, field: string, value: string) => {
    setLanguageData({
      ...languageData,
      [lang]: {
        ...languageData[lang],
        [section]: {
          ...languageData[lang][section as keyof LanguageData],
          [field]: value,
        },
      },
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('itineraries.basicInformation')}</h3>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                {t('itineraries.fields.title')} *
              </label>
              <input
                type="text"
                id="title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                {t('itineraries.fields.description')}
              </label>
              <div className="mt-1">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder={t('itineraries.fields.descriptionPlaceholder')}
                  disabled={loading}
                  height="150px"
                />
              </div>
            </div>

            <FileUpload
              type="logo"
              currentUrl={formData.logo}
              onUpload={(url) => setFormData({ ...formData, logo: url })}
              onDelete={() => setFormData({ ...formData, logo: '' })}
              disabled={loading}
            />

            <div>
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700">
                {t('itineraries.fields.visibility')} *
              </label>
              <select
                id="visibility"
                value={formData.visibility}
                onChange={(e) => setFormData({ ...formData, visibility: e.target.value as 'public' | 'restricted' })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="public">{t('itineraries.visibility.public')}</option>
                <option value="restricted">{t('itineraries.visibility.restricted')}</option>
              </select>
            </div>

            {formData.visibility === 'restricted' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('itineraries.fields.visibilityStrategy')}
                </label>
                <div className="space-y-2">
                  <label htmlFor="strategy-mego" className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="strategy-mego"
                      checked={formData.visibility_strategy.includes('mego')}
                      onChange={(e) => {
                        const currentStrategies = Array.isArray(formData.visibility_strategy) 
                          ? formData.visibility_strategy 
                          : [];
                        let newStrategies: string[];
                        if (e.target.checked) {
                          // Add mego if not already present
                          newStrategies = currentStrategies.includes('mego')
                            ? currentStrategies
                            : [...currentStrategies, 'mego'];
                        } else {
                          // Remove mego
                          newStrategies = currentStrategies.filter(s => s !== 'mego');
                        }
                        setFormData(prev => ({
                          ...prev,
                          visibility_strategy: newStrategies,
                        }));
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 block text-sm text-gray-700">
                      {t('itineraries.strategies.mego')}
                    </span>
                  </label>
                  <label htmlFor="strategy-hiragusa" className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="strategy-hiragusa"
                      checked={formData.visibility_strategy.includes('hiragusa')}
                      onChange={(e) => {
                        const currentStrategies = Array.isArray(formData.visibility_strategy) 
                          ? formData.visibility_strategy 
                          : [];
                        let newStrategies: string[];
                        if (e.target.checked) {
                          // Add hiragusa if not already present
                          newStrategies = currentStrategies.includes('hiragusa')
                            ? currentStrategies
                            : [...currentStrategies, 'hiragusa'];
                        } else {
                          // Remove hiragusa
                          newStrategies = currentStrategies.filter(s => s !== 'hiragusa');
                        }
                        setFormData(prev => ({
                          ...prev,
                          visibility_strategy: newStrategies,
                        }));
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 block text-sm text-gray-700">
                      {t('itineraries.strategies.hiragusa')}
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Audio Steps */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('itineraries.audioSteps')}</h3>
            <button
              type="button"
              onClick={addAudioStep}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              {t('itineraries.addStep')}
            </button>
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable droppableId="audio-steps">
              {(provided, snapshot) => (
                <div 
                  {...provided.droppableProps} 
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {audioSteps.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {t('itineraries.noSteps')}
                    </div>
                  ) : (
                    audioSteps.map((step, index) => {
                      const isExpanded = expandedSteps.has(step.id);
                      const stepTitle = step.title[Object.keys(SUPPORTED_LANGUAGES)[0]] || `${t('itineraries.fields.step')} ${step.id}`;
                      
                      return (
                        <Draggable key={`step-${step.id}`} draggableId={`step-${step.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`border border-gray-200 rounded-lg overflow-hidden ${
                              snapshot.isDragging ? 'shadow-lg' : ''
                            }`}
                          >
                            {/* Accordion Header */}
                            <div 
                              className="flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                              onClick={() => toggleStepExpansion(step.id)}
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GripVertical className="h-4 w-4 text-gray-400" />
                                </div>
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-gray-500" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-gray-500" />
                                )}
                                <h4 className="text-md font-medium text-gray-900">
                                  {stepTitle}
                                </h4>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeAudioStep(index);
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors"
                                title={t('itineraries.remove')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="p-4 border-t border-gray-200">

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {Object.entries(SUPPORTED_LANGUAGES).map(([langCode, langName]) => (
                    <div key={langCode} className="space-y-4">
                      <h5 className="text-sm font-medium text-gray-700">{langName}</h5>
                      
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-medium text-gray-600">{t('itineraries.fields.title')}</label>
                          <TranslationHelper
                            currentText={step.title[langCode] || ''}
                            currentLanguage={langCode}
                            allLanguageData={step.title}
                            onTranslationComplete={(translatedText, targetLang) => updateAudioStep(index, 'title', { [targetLang]: translatedText })}
                            disabled={loading}
                          />
                        </div>
                        <input
                          type="text"
                          value={step.title[langCode] || ''}
                          onChange={(e) => updateAudioStep(index, 'title', { [langCode]: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-medium text-gray-600">{t('itineraries.fields.description')}</label>
                          <TranslationHelper
                            currentText={step.description[langCode] || ''}
                            currentLanguage={langCode}
                            allLanguageData={step.description}
                            onTranslationComplete={(translatedText, targetLang) => updateAudioStep(index, 'description', { [targetLang]: translatedText })}
                            disabled={loading}
                          />
                        </div>
                        <div className="mt-1">
                          <RichTextEditor
                            value={step.description[langCode] || ''}
                            onChange={(value) => updateAudioStep(index, 'description', { [langCode]: value })}
                            placeholder={t('itineraries.fields.stepDescriptionPlaceholder')}
                            disabled={loading}
                            height="120px"
                            className="text-sm"
                          />
                        </div>
                      </div>

                    </div>
                  ))}
                </div>

                {/* Audio Upload */}
                <div className="mt-4">
                  <AudioStepUpload
                    audioUrls={step.audioUrl}
                    onUpdate={(audioUrls) => updateAudioStep(index, 'audioUrl', audioUrls)}
                    disabled={loading}
                  />
                </div>

                {/* Step Image Upload */}
                <div className="mt-4">
                  <FileUpload
                    type="image"
                    currentUrl={step.imageUrl}
                    onUpload={(url) => updateAudioStep(index, 'imageUrl', url)}
                    onDelete={() => updateAudioStep(index, 'imageUrl', '')}
                    disabled={loading}
                    folder="step-images"
                  />
                </div>

                {/* Location */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-600 mb-2">{t('itineraries.fields.location')}</label>
                  <GoogleMapsSearch
                    onLocationSelect={(location) => updateAudioStep(index, 'location', {
                      latitude: location.latitude,
                      longitude: location.longitude,
                    })}
                    initialLocation={step.location}
                    placeholder={t('itineraries.fields.searchLocation')}
                  />
                </div>
                    </div>
                  )}
                          </div>
                        )}
                        </Draggable>
                      );
                    })
                  )}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </div>

        {/* Language Data */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('itineraries.languageData')}</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(SUPPORTED_LANGUAGES).map(([langCode, langName]) => (
              <div key={langCode} className="space-y-4">
                <h4 className="text-md font-medium text-gray-700">{langName}</h4>
                
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-600">{t('itineraries.fields.welcomeTitle')}</label>
                    <TranslationHelper
                      currentText={languageData[langCode]?.welcome?.title || ''}
                      currentLanguage={langCode}
                      allLanguageData={Object.keys(SUPPORTED_LANGUAGES).reduce((acc, lang) => {
                        acc[lang] = languageData[lang]?.welcome?.title || '';
                        return acc;
                      }, {} as Record<string, string>)}
                      onTranslationComplete={(translatedText, targetLang) => updateLanguageData(targetLang, 'welcome', 'title', translatedText)}
                      disabled={loading}
                    />
                  </div>
                  <input
                    type="text"
                    value={languageData[langCode]?.welcome?.title || ''}
                    onChange={(e) => updateLanguageData(langCode, 'welcome', 'title', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-600">{t('itineraries.fields.welcomeSubtitle')}</label>
                    <TranslationHelper
                      currentText={languageData[langCode]?.welcome?.subtitle || ''}
                      currentLanguage={langCode}
                      allLanguageData={Object.keys(SUPPORTED_LANGUAGES).reduce((acc, lang) => {
                        acc[lang] = languageData[lang]?.welcome?.subtitle || '';
                        return acc;
                      }, {} as Record<string, string>)}
                      onTranslationComplete={(translatedText, targetLang) => updateLanguageData(targetLang, 'welcome', 'subtitle', translatedText)}
                      disabled={loading}
                    />
                  </div>
                  <input
                    type="text"
                    value={languageData[langCode]?.welcome?.subtitle || ''}
                    onChange={(e) => updateLanguageData(langCode, 'welcome', 'subtitle', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('itineraries.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium"
          >
            {loading ? t('itineraries.saving') : (itinerary ? t('itineraries.update') : t('itineraries.create'))}
          </button>
        </div>
      </form>
    </div>
  );
};
